import { Component, Input, OnInit } from '@angular/core';
import { UpdateComponent } from '@app/shared/components/base/update.component';
import * as _ from 'lodash-es';
import { Vehicle } from '@app/shared/models/vehicle';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { OP, OpData } from '@app/core/services/base/base-service-with-bundle.service';

export interface Mileage {
  mileage: string;
  rse: string;
  unit: any;
}

@Component({
  selector: 'mileage-input',
  templateUrl: './mileage.component.html',
  styleUrls: ['./mileage.component.scss']
})
export class MileageComponent extends UpdateComponent
                              implements OnInit {

  private originalCompactMileage: Mileage;
  private updatedCompactMileage: Mileage;

  @Input()
  public vehicle: Vehicle;

  public compact = true;
  public objectKeys = Object.keys;

  public mileageRSE = {
    READ: {
      key: 'read',
      trans: marker('vehicle.mileage.rse.read')
    },
    SPECIFIED: {
      key: 'specified',
      trans: marker('vehicle.mileage.rse.specified')
    },
    ESTIMATED: {
      key: 'estimated',
      trans: marker('vehicle.mileage.rse.estimated')
    }
  };

  public mileageUnits = {
    KM: {
      code: 'km',
      value: 'km',
      trans: marker('vehicle.mileage.unit.km')
    },
    MILES: {
      code: 'mls',
      value: 'miles',
      trans: marker('vehicle.mileage.unit.mls')
    },
    HOURS: {
      code: 'hrs',
      value: 'hours',
      trans: marker('vehicle.mileage.unit.hrs')
    }
  };

  public getUnitByCode(code: string): any {
    return this.mileageUnits.KM.code === code ? this.mileageUnits.KM :
           this.mileageUnits.MILES.code === code ? this.mileageUnits.MILES :
           this.mileageUnits.HOURS.code === code ? this.mileageUnits.HOURS :
           null;
  }

  get compactMileage(): string {
    return this.updatedCompactMileage.mileage;
  }

  set compactMileage(mileage: string) {
    this.updatedCompactMileage.mileage = mileage;
  }

  get compactMileageUnit(): string {
    return this.updatedCompactMileage.unit && this.updatedCompactMileage.unit.code;
  }

  set compactMileageUnit(mileageUnitCode: string) {
    this.updatedCompactMileage.unit = this.getUnitByCode(mileageUnitCode);
    this.compactMileageUpdate();
  }

  get compactMileageRse(): string {
    return this.updatedCompactMileage.rse;
  }

  set compactMileageRse(mileageRse: string) {
    this.updatedCompactMileage.rse = mileageRse;
    this.compactMileageUpdate();
  }

  get detailedReadUnitCode(): string {
    return this.vehicle.mileage_read_unit_code || this.mileageUnits.KM.code;
  }

  set detailedReadUnitCode(code: string) {
    this.vehicle.mileage_read_unit_code = code;
  }

  get detailedSpecifiedUnitCode(): string {
    return this.vehicle.mileage_specified_unit_code || this.mileageUnits.KM.code;
  }

  set detailedSpecifiedUnitCode(code: string) {
    this.vehicle.mileage_specified_unit_code = code;
  }

  get detailedEstimatedUnitCode(): string {
    return this.vehicle.mileage_estimated_unit_code || this.mileageUnits.KM.code;
  }

  set detailedEstimatedUnitCode(code: string) {
    this.vehicle.mileage_estimated_unit_code = code;
  }

  constructor() {
    super();
  }

  ngOnInit() {
    this.initCompactValues();
  }

  public toDetailedMileage(): void {
    this.compact = false;
  }

  private initCompactValues(): void {
    let counter = 0;
    let mileageRSE;

    if (this.vehicle.mileage_read) {
      counter++;
      mileageRSE = this.mileageRSE.READ;
    }

    if (this.vehicle.mileage_specified) {
      counter++;
      mileageRSE = this.mileageRSE.SPECIFIED;
    }

    if (this.vehicle.mileage_estimated) {
      counter++;
      mileageRSE = this.mileageRSE.ESTIMATED;
    }

    if (counter < 2) {
      if (counter === 0) {
        this.originalCompactMileage = {
          mileage: null,
          unit: null,
          rse: null
        };

        this.updatedCompactMileage = {
          mileage: null,
          unit: this.mileageUnits.KM,
          rse: this.mileageRSE.READ.key
        };
      } else if (counter === 1) {
        this.originalCompactMileage = {
          mileage: this.vehicle[`mileage_${mileageRSE.key}`],
          unit: this.getUnitByCode(this.vehicle[`mileage_${mileageRSE.key}_unit_code`]),
          rse: mileageRSE.key
        };
        this.updatedCompactMileage = _.cloneDeep(this.originalCompactMileage);
      }

      this.compact = true;
    } else {
      this.originalCompactMileage = null;
      this.compact = false;
    }
  }

  public compactMileageUpdate(): void {
    if (this.originalCompactMileage.rse !== this.updatedCompactMileage.rse) {
      const patches = new Array<OpData>();

      // Check if we have old values to remove
      // If not this was probably the first time mileage is being entered
      if (this.originalCompactMileage.rse) {
        // Remove the old mileage data
        patches.push({
          op: OP.remove,
          path: `vehicle/mileage_${this.originalCompactMileage.rse}`
        });

        // Remove the old unit code
        patches.push({
          op: OP.remove,
          path: `vehicle/mileage_${this.originalCompactMileage.rse}_unit_code`
        });

        // Remove the old unit translation
        patches.push({
          op: OP.remove,
          path: `vehicle/mileage_${this.originalCompactMileage.rse}_unit_value`
        });
      }

      // Add the new mileage
      patches.push({
        op: OP.replace,
        path: `vehicle/mileage_${this.updatedCompactMileage.rse}`,
        value: this.updatedCompactMileage.mileage
      });

      this.originalCompactMileage.mileage = this.updatedCompactMileage.mileage;

      // Add the new unit code
      patches.push({
        op: OP.replace,
        path: `vehicle/mileage_${this.updatedCompactMileage.rse}_unit_code`,
        value: this.updatedCompactMileage.unit.code
      });

      // Add the new unit
      patches.push({
        op: OP.replace,
        path: `vehicle/mileage_${this.updatedCompactMileage.rse}_unit_value`,
        value: this.updatedCompactMileage.unit.value
      });

      this.originalCompactMileage.unit = this.updatedCompactMileage.unit;
      this.originalCompactMileage.rse = this.updatedCompactMileage.rse;
      this.onMultiUpdate(patches);
    } else if (this.originalCompactMileage.unit.code !== this.updatedCompactMileage.unit.code) {
      const patches = new Array<OpData>();

      // Replace the unit code
      patches.push({
        op: OP.replace,
        path: `vehicle/mileage_${this.originalCompactMileage.rse}_unit_code`,
        value: this.updatedCompactMileage.unit.code
      });

      // Replace the unit value
      patches.push({
        op: OP.replace,
        path: `vehicle/mileage_${this.originalCompactMileage.rse}_unit_value`,
        value: this.updatedCompactMileage.unit.value
      });

      this.originalCompactMileage.unit = this.updatedCompactMileage.unit;

      this.onMultiUpdate(patches);
    } else if (this.originalCompactMileage.mileage !== this.updatedCompactMileage.mileage) {
      this.updatedCompactMileage.mileage = this.updatedCompactMileage.mileage !== '' ? this.updatedCompactMileage.mileage : null;
      this.onChange(`vehicle.mileage_${this.originalCompactMileage.rse}`, this.updatedCompactMileage.mileage);
    }
  }

  public detailedMileageUnitUpdate(rse: string, code: string): void {
    const patches = this.createMileageUnitUpdatePatches(rse, code);
    this.multiUpdated.emit(patches);
  }

  private createMileageUnitUpdatePatches(rse: string, code: string, patches?: OpData[]): OpData[] {
    const selectedUnit = this.getUnitByCode(code);

    if (!patches) {
      patches = new Array<OpData>();
    }

    // Replace the unit code
    patches.push({
      op: OP.replace,
      path: `vehicle/mileage_${rse}_unit_code`,
      value: selectedUnit.code
    });

    // Replace the unit value
    patches.push({
      op: OP.replace,
      path: `vehicle/mileage_${rse}_unit_value`,
      value: selectedUnit.value
    });

    return patches;
  }

  public detailedMileageUpdate(rse: string, mileage: number): void {
    const patches = this.createMileageUpdatePatches(rse, mileage);
    this.multiUpdated.emit(patches);
  }

  private createMileageUpdatePatches(rse: string, mileage: number, patches?: OpData[]): OpData[] {
    if (!patches) {
      patches = new Array<OpData>();
    }

    // Replace the mileage value
    patches.push({
      op: OP.replace,
      path: `vehicle/mileage_${rse}`,
      value: mileage
    });

    let originalCode: string;
    let code: string;

    if (rse === this.mileageRSE.READ.key) {
      originalCode = this.vehicle.mileage_read_unit_code;
      code = this.detailedReadUnitCode;
    } else if (rse === this.mileageRSE.SPECIFIED.key) {
      originalCode = this.vehicle.mileage_specified_unit_code;
      code = this.detailedSpecifiedUnitCode;
    } else if (rse === this.mileageRSE.ESTIMATED.key) {
      originalCode = this.vehicle.mileage_estimated_unit_code;
      code = this.detailedEstimatedUnitCode;
    }

    // Also create patches for updating the unit code and value
    // if we have a code that is different than the original code
    if (code !== originalCode) {
      patches = this.createMileageUnitUpdatePatches(rse, code, patches);
    }

    return patches;
  }

}
